import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../api/ApiClientContext';
import { BE_SECURE_HABIT_ID } from '../../constants/habits';
import styles from './InitiativeDetailsPage.module.scss';

const InitiativeDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const { mutate: selectHabit, isPending } = useMutation({
    mutationFn: async (habitId: string) => {
      return apiClient.api.selectHabitApiV1HabitsSelectPost({
        habit_id: habitId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userFlowStatus'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['programmes'] });
      navigate('/');
    },
    onError: (error) => {
      console.error('Error selecting habit:', error);
    },
  });

  const handleCancel = () => {
    navigate(-1);
  };

  const handleStartHabitAdoption = () => {
    selectHabit(BE_SECURE_HABIT_ID);
  };

  return (
    <div className={styles.initiativePage}>
      <main className={styles.content}>
        <div className={styles.card}>
          <h1 className={styles.title}>Be Secure</h1>
          <p className={styles.subtitle}>Module 1: Foundations of Everyday Security</p>

          <div className={styles.imageWrapper}>
            <img 
              src="/images/programme/module_2.jpg" 
              alt="Be Secure Initiative" 
              className={styles.image}
            />
          </div>

          <div className={styles.stats}>
            <div className={styles.stat}>
              <span className={styles.stat__value}>60 DAYS</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.stat__value}>1 BEHAVIOUR</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.stat__value}>1 HABIT</span>
            </div>
          </div>

          <div className={styles.description}>
            <p className={styles.description__lead}>
              Building a culture where security awareness powers resilience, trust, and performance.
            </p>

            <p>
              The Be Secure initiative is a six-month journey designed to strengthen secure thinking 
              and behaviour across Northbridge. By turning everyday digital actions into opportunities 
              to practise security mindfulness, participants will help shape a culture of vigilance, 
              accountability, and confidence in a connected workplace.
            </p>

            <p>
              Through facilitated learning modules, peer challenges, and real-world habit tracking, 
              participants will explore how security shows up in daily decisions from managing data 
              responsibly, identifying risks, to proactively protecting information assets.
            </p>

            <p>
              This initiative goes beyond compliance; it's about cultivating a sustainable mindset of 
              security awareness that becomes second nature. Participants will gain practical skills, 
              connect with colleagues on shared challenges, and contribute to a safer, more resilient 
              organisation.
            </p>
          </div>
        </div>
      </main>

      <div className={styles.bottomActions}>
        <button className={styles.actionButton} onClick={handleCancel}>
         Back
        </button>
        <button 
          className={styles.actionButton} 
          onClick={handleStartHabitAdoption}
          disabled={isPending}
        >
          {isPending ? 'Starting...' : 'Start habit adoption'}
        </button>
      </div>
    </div>
  );
};

export default InitiativeDetailsPage;
